/**
 * Recipe I/O Module
 * 
 * Handles recipe image generation, batch import, and copy operations.
 * Extracted from recipeService.ts for better modularity.
 */

import { Recipe, RecipeFormData, RecipeSource, RecipeCategory } from '../../types/recipe';
import { AssistantResponse } from '@/types/assistantTypes';
import { PROMPT_TEMPLATES } from '@/prompts/recipePrompts';
import { generateContent } from '../geminiService';
import { generateRecipeCleanup } from '../ai/aiGateway';
import { modelService } from '../modelService';
import { parseMarkdownToRecipeForm } from './recipeParsing';

/**
 * Generates an image prompt for the given recipe.
 */
export async function generateImagePrompt(recipe: Recipe): Promise<AssistantResponse> {
    if (!recipe) {
        return { success: false, error: "Recipe data is required." };
    }

    // Use utility functions to format parts of the recipe for the prompt
    const ingredientsSummary = formatIngredientsForPrompt(recipe);
    const instructionsSummary = recipe.method
        ? recipe.method.split('\n').filter(line => line.trim()).slice(0, 2).join(' ')
        : 'No instructions available';

    const recipeDetails = `
    Title: ${recipe.title}
    Key Ingredients: ${ingredientsSummary}
    First few instructions: ${instructionsSummary}
  `;

    const prompt = PROMPT_TEMPLATES.IMAGE_GENERATION(recipeDetails);
    const model = await modelService.getModelForFeature('textGeneration' as any);
    const result = await generateContent(prompt, model);

    if (result.success && result.text) {
        return { success: true, message: "Image prompt generated.", data: result.text.trim() };
    } else {
        return { success: false, error: result.error || "Failed to generate image prompt." };
    }
}

/**
 * Generates an image for a recipe that hasn't been saved yet (preview mode).
 * Uses just the title and description to create a quick preview image.
 */
export async function generateRecipeImage(
    title: string,
    description?: string
): Promise<{ success: boolean; imageUrl?: string; error?: string }> {
    if (!title || title.trim().length < 3) {
        return { success: false, error: "Recipe title is required for image generation." };
    }

    try {
        // Create a minimal recipe object for the image generation pipeline
        const minimalRecipe: Recipe = {
            id: 'preview-' + Date.now(),
            title: title.trim(),
            description: description?.trim() || '',
            ingredients: '',
            method: '',
            notes: '',
            category: undefined, // Will be set later by user
            tags: [],
            dateCreated: new Date().toISOString(),
            dateModified: new Date().toISOString(),
            user_id: '',
            source: RecipeSource.User,
            isFavorite: false,
            share: false,
        };

        // Import and use the existing image generation pipeline
        const { generateImageDirectlyForRecipe } = await import('../geminiService');

        const result = await generateImageDirectlyForRecipe(minimalRecipe);

        if (result.success && result.imageData) {
            return {
                success: true,
                imageUrl: result.imageData // This is already a base64 data URL
            };
        } else {
            return {
                success: false,
                error: result.error || 'Failed to generate image'
            };
        }
    } catch (error) {
        return {
            success: false,
            error: error instanceof Error ? error.message : 'Image generation failed'
        };
    }
}

/**
 * Handles a request to copy an existing recipe, potentially with AI modifications.
 * For now, it focuses on a simple duplication. AI modification can be added later.
 */
export async function handleRecipeCopyRequest(
    originalRecipe: Recipe,
    _modificationRequest?: string // Added optional second parameter
): Promise<AssistantResponse> {

    try {
        // Step 1: Create a basic copy of the recipe data for the form
        const copiedFormData = createRecipeCopy(originalRecipe);

        // Step 2: (Future) If modificationRequest is provided, send to AI
        // For now, we directly use the copied data.

        // Step 3: Store in sessionStorage for RecipeForm.tsx to pick up as a new recipe
        sessionStorage.setItem('recipeFormData', JSON.stringify(copiedFormData));

        return {
            success: true,
            message: "Recipe copied successfully. Ready for editing.",
            data: {
                formData: copiedFormData,
                originalTitle: originalRecipe.title // Added for UI feedback
                // No AI interaction in this basic copy, so no structuredMarkdown from AI
            }
        };
    } catch (error: any) {
        return {
            success: false,
            error: error.message || "An unexpected error occurred during recipe copy.",
        };
    }
}

/**
 * Batch import: Uses LLM to extract and structure multiple recipes from arbitrary text.
 * Returns an array of parsed RecipeFormData objects.
 */
export async function handleBatchRecipeImport(
    rawText: string,
    model?: string,
    metadata?: { persona?: string; dietaryTags?: string[]; category?: string }
): Promise<{ success: boolean; recipes: Partial<RecipeFormData>[]; error?: string; rawText?: string }> {
    if (!rawText || rawText.trim().length < 10) {
        return { success: false, recipes: [], error: "Please provide more recipe content to work with." };
    }

    // Improved prompt for robust, format-agnostic extraction
    const systemPrompt = `You are a recipe expert. Your task is to extract EVERY recipe from the following text, regardless of formatting. The input may contain recipes separated by any of the following:
- Markdown headers of any level (e.g., #, ##, ###, or bolded **Section** headers)
- Plain text titles, section breaks, or inconsistent formatting
- Clean or messy markdown, web copy-paste, or mixed formats

For EACH recipe you find, output it in this EXACT canonical markdown format, and separate each recipe with a line containing only three dashes (---):

# [Recipe Title]
## Description
A concise, appetizing, and non-empty description of the dish. This section is MANDATORY and must never be empty. If the source text does not provide a description, write a plausible one based on the recipe content.
## Ingredients
[List all ingredients, preserving any groupings and their original order.]
## Method
[List all instructions, preserving any groupings and their original order.]
## Notes
[Include any supplementary text, tips, or notes.]

---

CRITICAL RULES:
1. **BOUNDARIES:** Treat any markdown header (of any level), bolded section, or plausible recipe title as the start of a new recipe. Do NOT skip any section that could be a recipe.
2. **COMPLETENESS:** Extract ALL recipes and ALL their content. Never summarize, merge, or skip recipes, even if the formatting is inconsistent.
3. **CANONICAL OUTPUT:** Output each recipe in the canonical format above, separated by ---. Do not include any extra commentary or summary.
4. **ROBUSTNESS:** If a recipe is missing a section (e.g., description), generate a plausible one based on the content. Never leave required sections empty.

Now, extract EVERY recipe from the following text and output each in the canonical format, separated by ---:

${rawText}`;

    const result = await generateRecipeCleanup(systemPrompt, 'handleBatchRecipeImport');
    if (!result.success || !result.text) {
        return { success: false, recipes: [], error: result.error || 'LLM failed to extract recipes.', rawText: result.text };
    }

    // Split on --- and parse each recipe (robust: allow extra whitespace, dashes, or lines)
    const normalized = result.text.replace(/\r\n?/g, '\n');
    // Accept lines with 3+ dashes, possibly surrounded by whitespace
    const recipeBlocks = normalized.split(/^\s*-{3,}\s*$/gm).map(chunk => chunk.trim()).filter(Boolean);
    const recipes = recipeBlocks.map(chunk => {
        const data = parseMarkdownToRecipeForm(chunk, metadata);
        // Optionally assign category if provided and valid
        if (metadata?.category && [
            'Asian', 'Sri Lankan', 'Mexican', 'Latin American', 'Desserts', 'Drinks', 'Sauces', 'Other', 'To Try'
        ].includes(metadata.category)) {
            data.category = metadata.category as RecipeCategory;
        }
        return data;
    });

    // Developer logging

    return { success: true, recipes };
}

// ============================================================================
// HELPER FUNCTIONS
// ============================================================================

/**
 * A simple utility to format recipe ingredients into a string.
 */
function formatIngredientsForPrompt(recipe: Recipe): string {
    if (!recipe.ingredients) return '';

    return recipe.ingredients
        .split('\n')
        .filter(line => line.trim().length > 0)
        .map(line => line.trim().replace(/^[-*+]\s*/, ''))
        .slice(0, 5)
        .join(', ');
}

/**
 * Creates a new RecipeFormData object based on an existing recipe,
 * suitable for pre-filling the recipe form for a new (copied) recipe.
 * It resets fields like ID, created_at, and user_id.
 */
function createRecipeCopy(originalRecipe: Recipe): Partial<RecipeFormData> {
    const copiedData: Partial<RecipeFormData> = {
        // Core content
        title: `${originalRecipe.title} (Copy)`,
        description: originalRecipe.description,
        ingredients: originalRecipe.ingredients,
        method: originalRecipe.method,
        category: originalRecipe.category,
        tags: originalRecipe.tags ? [...originalRecipe.tags] : [], // Deep copy tags array
        source: RecipeSource.Copied,

        // Metadata & Notes
        notes: originalRecipe.notes,

        // Fields to be reset or set to default for a new recipe
        isFavorite: false, // Copied recipe isn't a favorite by default
        share: true,       // Default to shareable, user can change
        image_url: originalRecipe.image_url, // Keep original image for the copy initially
        has_custom_image: originalRecipe.has_custom_image,

    };
    return copiedData;
}
